/**
 * ConstellationContextSelector — Dropdown picker for context profiles
 * that control which fields are promoted in the field constellation.
 *
 * Shows profile name, description, and vertical tags.
 * "Clear context" button to return to neutral view.
 */

import React from 'react';
import { Group, Select, Text, Badge, Button, Stack } from '@mantine/core';

import type { FieldContextProfile } from './hooks/usePrimitiveConstellation';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ConstellationContextSelectorProps {
  /** Available context profiles. */
  profiles: FieldContextProfile[];
  /** Currently active context ID (null = no context). */
  activeContextId: string | null;
  /** Callback when context changes. */
  onChange: (contextId: string | null) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ConstellationContextSelector({
  profiles,
  activeContextId,
  onChange,
}: ConstellationContextSelectorProps) {
  const selectData = profiles.map((p) => ({
    value: p.contextId,
    label: p.name,
  }));

  const activeProfile = profiles.find((p) => p.contextId === activeContextId);

  return (
    <Stack gap="xs">
      <Group gap="sm" align="flex-end">
        <Select
          label="Context Profile"
          placeholder="Select a context to highlight relevant fields"
          data={selectData}
          value={activeContextId}
          onChange={(value) => onChange(value)}
          clearable
          searchable
          size="sm"
          style={{ flex: 1 }}
        />
        {activeContextId && (
          <Button
            size="sm"
            variant="subtle"
            color="gray"
            onClick={() => onChange(null)}
          >
            Clear
          </Button>
        )}
      </Group>

      {activeProfile?.description && (
        <Text size="xs" c="dimmed">
          {activeProfile.description}
        </Text>
      )}

      {activeProfile && activeProfile.verticals.length > 0 && (
        <Group gap={4}>
          {activeProfile.verticals.map((v) => (
            <Badge key={v} variant="light" size="xs" color="blue">
              {v}
            </Badge>
          ))}
          <Text size="xs" c="dimmed">
            {activeProfile.promotedFields.length} fields promoted
          </Text>
        </Group>
      )}
    </Stack>
  );
}
